import { Matrix4 } from 'three';
import type { WorldFeature } from '../types';
import { createCitizenBatch } from './citizens/citizenBatch';
import { citizenGeometry } from './citizens/citizenShapes';
import { clipPoseAt, walkPose } from './citizens/citizenPose';
import { groundOf } from './crowd/walkNet';
import { lineOf } from './crowd/walkLine';
import { WALKING } from './crowd/crowdSim';
import { civicVisitorAt, planCivicActivity } from './civicPlan';
import { createHeadings } from './heading';

export const civicFeature: WorldFeature = {
  id: 'stadhuis-activity',
  label: 'Clerks, petitioners and messengers',
  weight: 0.3,
  build(ctx) {
    const asset = ctx.citizens,
      crowd = ctx.lifePlan?.crowd;
    if (!asset || !crowd) return { objects: [], animated: [], obstacles: [], counts: {} };
    const plan = planCivicActivity(ctx.layout, crowd, groundOf(ctx.layout, ctx.site, ctx.lots));
    const figures = asset.figures.filter((f) =>
      ['clerk', 'merchant', 'burgher', 'porter', 'watchman'].includes(f.trade)
    );
    if (!plan.offices.length || !figures.length)
      return { objects: [], animated: [], obstacles: [], counts: {} };
    ctx.materials.setCitizenPoses(asset.poses, asset.bones.length);
    const batch = createCitizenBatch(
      ctx.materials.painted(),
      figures.map((f) => citizenGeometry(asset, f.lods[0]!)),
      20,
      'stadhuis-clerks-visitors-messengers'
    );
    const pick = (trade: string, i: number) => {
      const choices = figures.flatMap((f, k) => (f.trade === trade ? [k] : []));
      return choices[i % choices.length]!;
    };
    const line = (route: readonly (readonly [number, number])[]) =>
      lineOf(
        route.map(([x, y]) => [x, y, ctx.site.queries.groundHeightAt([x, y])]),
        WALKING.cornerM
      );
    const visits = plan.offices.flatMap((office, o) =>
      office.routes.map((route, i) => {
        const trade = i < 3 ? (i % 2 ? 'merchant' : 'burgher') : i < 5 ? 'clerk' : 'porter';
        return {
          office: o,
          figure: pick(trade, i + o),
          line: line(route),
          speed: trade === 'clerk' ? 1.35 : trade === 'porter' ? 0.85 : 1.05,
          phase: (i + o * 0.41) / office.routes.length,
        };
      })
    );
    const patrol = plan.patrol.length ? line(plan.patrol) : undefined,
      base = new Matrix4();
    const headings = createHeadings(visits.length + 2);
    let day = true;
    const draw = (
      figure: number,
      x: number,
      y: number,
      height: number,
      heading: number,
      pose: number
    ) => {
      base.makeRotationY(heading).setPosition(x, height, -y);
      batch.draw(figure, base, pose);
    };
    const animated = {
      id: 'town-hall-business',
      setTimeOfDay(_minutes: number, activity: number) {
        day = activity > 0.05;
      },
      update(seconds: number) {
        headings.begin(seconds);
        batch.begin();
        if (day) {
          const attended = new Set<number>();
          for (const [i, visit] of visits.entries()) {
            const p = civicVisitorAt(visit.line, seconds, visit.phase, visit.speed),
              figure = figures[visit.figure]!;
            if (p.serving) attended.add(visit.office);
            const pose = p.moving
              ? walkPose(asset.clips[figure.clips.walk]!, p.walked, 1)
              : clipPoseAt(asset.clips[figure.clips.work]!, seconds);
            draw(visit.figure, p.x, p.y, p.ground, headings.at(i, p.heading), pose);
          }
          plan.offices.forEach((office, i) => {
            const figure = pick('clerk', i),
              f = figures[figure]!;
            draw(
              figure,
              ...office.clerk,
              ctx.site.queries.groundHeightAt(office.clerk),
              office.facing,
              clipPoseAt(asset.clips[attended.has(i) ? f.clips.work : f.clips.idle]!, seconds + i)
            );
          });
          if (patrol)
            for (let i = 0; i < 2; i++) {
              const figure = pick('watchman', i),
                f = figures[figure]!,
                p = civicVisitorAt(patrol, seconds, i * 0.5, 0.75, 3);
              draw(
                figure,
                p.x,
                p.y,
                p.ground,
                headings.at(visits.length + i, p.heading),
                p.moving
                  ? walkPose(asset.clips[f.clips.walk]!, p.walked, 1)
                  : clipPoseAt(asset.clips[f.clips.idle]!, seconds)
              );
            }
        }
        batch.end();
      },
    };
    animated.update(0);
    return {
      objects: [batch.mesh],
      animated: [animated],
      obstacles: [],
      counts: {
        civicClerks: plan.offices.length,
        civicVisitors: visits.filter(
          (v) => figures[v.figure]!.trade === 'merchant' || figures[v.figure]!.trade === 'burgher'
        ).length,
        civicCouriers: visits.filter((v) => figures[v.figure]!.trade === 'clerk').length,
        civicPorters: visits.filter((v) => figures[v.figure]!.trade === 'porter').length,
        civicGuards: patrol ? 2 : 0,
      },
    };
  },
};
